import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useEffect, useState } from 'react';
import { getOrganisationById, updateOrganisation } from '../api/organisationAPI';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';

const CompanyLayer = () => {
  const [orgData, setOrgData] = useState(null);
  const [originalOrgData, setOriginalOrgData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [imagePreview, setImagePreview] = useState('assets/images/user-grid/user-grid-img13.png');

  // Fetch organisation details on mount
  const userProfile = useUserProfile();

  useEffect(() => {
    if (userProfile?.org_id) {
      getOrganisationById(userProfile.org_id)
        .then((data) => {
          setOrgData(data);
          setOriginalOrgData(data);
          if (data.org_logo) {
            setImagePreview(data.org_logo);
          }
        })
        .catch((error) =>
          console.error('Error fetching organisation:', error)
        );
    }
  }, [userProfile]);

  // Update image preview and orgData when a new file is selected
  const readURL = (input) => {
    if (input.target.files && input.target.files[0]) {
      const file = input.target.files[0];
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert("File is too large. Maximum allowed size is 5MB.");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target.result;
        setImagePreview(base64Image);
        // Update the organisation data with the new logo
        setOrgData((prev) => ({ ...prev, org_logo: base64Image }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle input changes; input id must match property name
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setOrgData({ ...orgData, [id]: value });
  };

  // Toggle editing on and off
  const toggleEdit = () => {
    setEditMode(true);
  };

  // Reset the form to the last saved state
  const handleReset = () => {
    setOrgData(originalOrgData);
    setImagePreview(originalOrgData.org_logo || 'assets/images/user-grid/user-grid-img13.png');
  };

  // Save changes to backend
  const handleSave = (e) => {
    e.preventDefault();
    updateOrganisation(orgData.id, orgData)
      .then((updatedOrg) => {
        setOrgData(updatedOrg);
        setOriginalOrgData(updatedOrg);
        setEditMode(false);
      })
      .catch((error) => console.error('Error updating organisation:', error));
  };

  if (!orgData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="card h-100 p-0 rounded-0 overflow-hidden">
      <div className="card-body p-0" style={{ "min-height": '600px' }}>
        <form onSubmit={handleSave}>
          <div className="row">
            <h6 className="text-md text-primary-light mb-16">Organisation or Team Logo</h6>
            {/* Upload Image Start */}
            <div className="mb-24 mt-16">
              <div className="avatar-upload">
                {editMode && (
                  <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                    <input
                      type="file"
                      id="imageUpload"
                      accept=".png, .jpg, .jpeg"
                      hidden
                      onChange={readURL}
                    />
                    <label
                      htmlFor="imageUpload"
                      className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle"
                    >
                      <Icon icon="solar:camera-outline" className="icon" />
                    </label>
                  </div>
                )}
                <div className="avatar-preview">
                  <div
                    id="imagePreview"
                    style={{
                      backgroundImage: `url(${imagePreview})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      width: '150px',
                      height: '150px'
                    }}
                  />
                </div>
              </div>
            </div>
            {/* Organisation Details */}
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_name" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Organisation or Team Name <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="org_name"
                  placeholder="Enter Name"
                  value={orgData.org_name || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_description" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Organisation Description
                </label>
                <textarea
                  className="form-control radius-8"
                  id="org_description"
                  placeholder="Enter description"
                  value={orgData.org_description || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_cause" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Organisation Cause
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="org_cause"
                  placeholder="Enter cause"
                  value={orgData.org_cause || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_website" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Website or Facebook Page
                </label>
                <div className="input-group">
                  <input
                    type="url"
                    className="form-control radius-8"
                    id="org_website"
                    placeholder="Website or Facebook Link"
                    value={orgData.org_website || ""}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                  {orgData.org_website && (
                    <a 
                      href={orgData.org_website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-outline-primary"
                    >
                      <Icon icon="ic:baseline-launch" className="fs-5 text-primary mb-2" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="col-sm-12">
              <div className="mb-20">
                <label htmlFor="org_address" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Street Number and Name <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="org_address"
                  placeholder="Enter Your Street Number and Name"
                  value={orgData.org_address || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_phone" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Phone Number
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="org_phone"
                  placeholder="Enter phone number"
                  value={orgData.org_phone || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_country" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Country <span className="text-danger-600">*</span>
                </label>
                <select
                  className="form-control radius-8 form-select"
                  id="org_country"
                  value={orgData.org_country || "Select Country"}
                  onChange={handleInputChange}
                  disabled={!editMode}
                >
                  <option value="Select Country" disabled>
                    Select Country
                  </option>
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                </select>
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_city" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  City or Suburb <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="org_city"
                  placeholder="Enter City or Suburb"
                  value={orgData.org_city || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="col-sm-6">
              <div className="mb-20">
                <label htmlFor="org_zip" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Post Code <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="org_zip"
                  placeholder="Post Code"
                  value={orgData.org_zip || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                />
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
              {editMode ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary text-md px-24 py-12 radius-8"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                  >
                    Save
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                  onClick={toggleEdit}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyLayer;
